import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Lock, X } from 'lucide-react';
import { APPLICATION_HISTORY_COLUMNS } from '../../../constants/fundingApplicationOptions';
import {
  getColumnMeta,
  insertColumnBeforeActions,
  saveApplicationHistoryColumns,
} from './applicationHistoryColumnConfig';

export function reorderColumnList(columns, sourceId, targetId) {
  if (!sourceId || !targetId || sourceId === targetId) return columns;
  const sourceIndex = columns.findIndex((column) => column.id === sourceId);
  const targetIndex = columns.findIndex((column) => column.id === targetId);
  if (sourceIndex < 0 || targetIndex < 0) return columns;
  const next = [...columns];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

const FundingManageFieldsPanel = ({ open, onOpenChange, columnConfig, onApply }) => {
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(columnConfig);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const draggedColumnIdRef = useRef(null);

  useEffect(() => {
    if (open) setDraft(columnConfig);
  }, [open, columnConfig]);

  useEffect(() => {
    if (!open) {
      draggedColumnIdRef.current = null;
      setDraggedColumnId(null);
      setDropTargetId(null);
    }
  }, [open]);

  const categories = useMemo(() => {
    const map = {};
    APPLICATION_HISTORY_COLUMNS.forEach((col) => {
      if (!map[col.category]) map[col.category] = [];
      map[col.category].push(col);
    });
    return map;
  }, []);

  const filteredDraft = useMemo(() => {
    if (!search) return draft;
    const q = search.toLowerCase();
    return draft.filter((col) => {
      const meta = getColumnMeta(col.id);
      return meta?.label?.toLowerCase().includes(q);
    });
  }, [draft, search]);

  const hiddenFields = useMemo(
    () => draft.filter((col) => !col.visible && col.id !== 'actions'),
    [draft],
  );

  const toggleVisibility = (id) => {
    const meta = getColumnMeta(id);
    if (meta?.lockedVisible) return;
    setDraft((prev) => prev.map((col) => (
      col.id === id ? { ...col, visible: !col.visible } : col
    )));
  };

  const reorderColumn = useCallback((sourceId, targetId) => {
    setDraft((current) => reorderColumnList(current, sourceId, targetId));
    draggedColumnIdRef.current = null;
    setDraggedColumnId(null);
    setDropTargetId(null);
  }, []);

  const beginDrag = useCallback((columnId, event) => {
    draggedColumnIdRef.current = columnId;
    setDraggedColumnId(columnId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, []);

  const endDrag = useCallback(() => {
    draggedColumnIdRef.current = null;
    setDraggedColumnId(null);
    setDropTargetId(null);
  }, []);

  const stopRowDrag = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const moveColumn = (id, direction) => {
    setDraft((prev) => {
      const index = prev.findIndex((col) => col.id === id);
      if (index < 0) return prev;
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addField = (id) => {
    setDraft((prev) => insertColumnBeforeActions(prev, id));
  };

  const handleApply = () => {
    const normalized = draft.map((col) => {
      const meta = getColumnMeta(col.id);
      return {
        id: col.id,
        visible: meta?.lockedVisible ? true : col.visible,
      };
    });
    saveApplicationHistoryColumns(normalized);
    onApply?.(normalized);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <aside
      className="funding-manage-fields-panel funding-fields-panel"
      data-testid="funding-manage-fields-panel"
    >
      <div className="funding-manage-fields-panel__head">
        <div className="funding-manage-fields-panel__head-top">
          <div>
            <h3 className="funding-manage-fields-panel__title">Manage fields</h3>
            <p className="funding-manage-fields-panel__subtitle">
              Show, hide, and reorder application history columns.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="funding-manage-fields-panel__close"
            aria-label="Close manage fields panel"
          >
            <X className="size-4" />
          </button>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search fields…"
          className="funding-manage-fields-panel__search funding-manage-fields-search"
        />
      </div>

      <div className="funding-manage-fields-panel__body">
        <div className="funding-manage-fields-panel__section">
          <h4 className="funding-manage-fields-panel__section-label">Column order</h4>
          <div className="funding-manage-fields-panel__rows" data-testid="funding-field-manager-rows">
            {filteredDraft.map((col) => {
              const meta = getColumnMeta(col.id);
              if (!meta) return null;
              const draftIndex = draft.findIndex((item) => item.id === col.id);
              const canMoveUp = draftIndex > 0;
              const canMoveDown = draftIndex >= 0 && draftIndex < draft.length - 1;
              const isDragging = draggedColumnId === col.id;
              const isDropTarget = dropTargetId === col.id && draggedColumnId !== col.id;
              return (
                <div
                  key={col.id}
                  draggable
                  onDragStart={(event) => beginDrag(col.id, event)}
                  onDragEnd={endDrag}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (dropTargetId !== col.id) setDropTargetId(col.id);
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget) && dropTargetId === col.id) {
                      setDropTargetId(null);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const sourceId = draggedColumnIdRef.current
                      || event.dataTransfer.getData('text/plain')
                      || draggedColumnId;
                    reorderColumn(sourceId, col.id);
                  }}
                  className={[
                    'funding-field-manager-row',
                    col.visible ? '' : 'is-hidden-column',
                    isDragging ? 'is-dragging' : '',
                    isDropTarget ? 'is-drop-target' : '',
                  ].filter(Boolean).join(' ')}
                  data-testid={`field-manager-row-${col.id}`}
                >
                  <span
                    className="funding-field-manager-row__grip"
                    aria-hidden="true"
                    title="Drag to reorder"
                  >
                    <GripVertical className="size-4" />
                  </span>
                  <label
                    className="funding-field-manager-row__label-wrap"
                    onMouseDown={stopRowDrag}
                    onDragStart={(event) => event.preventDefault()}
                  >
                    <input
                      type="checkbox"
                      checked={col.visible}
                      disabled={meta.lockedVisible}
                      onChange={() => toggleVisibility(col.id)}
                      aria-label={`Show ${meta.label} column`}
                      draggable={false}
                      onMouseDown={stopRowDrag}
                    />
                    <span className="funding-field-manager-row__label">{meta.label}</span>
                  </label>
                  <div className="funding-field-manager-row__actions">
                    {meta.lockedVisible ? (
                      <span className="funding-field-manager-row__locked" title="This field stays visible">
                        <Lock className="size-3" />
                        Locked
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="funding-field-manager-row__move"
                      onClick={() => moveColumn(col.id, -1)}
                      disabled={!canMoveUp}
                      aria-label={`Move ${meta.label} up`}
                      draggable={false}
                      onMouseDown={stopRowDrag}
                      onDragStart={(event) => event.preventDefault()}
                    >
                      <ChevronUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      className="funding-field-manager-row__move"
                      onClick={() => moveColumn(col.id, 1)}
                      disabled={!canMoveDown}
                      aria-label={`Move ${meta.label} down`}
                      draggable={false}
                      onMouseDown={stopRowDrag}
                      onDragStart={(event) => event.preventDefault()}
                    >
                      <ChevronDown className="size-4" />
                    </button>
                    <span className="funding-field-manager-row__position">{draftIndex + 1}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {hiddenFields.length > 0 ? (
          <div className="funding-manage-fields-panel__section">
            <h4 className="funding-manage-fields-panel__section-label">Add fields</h4>
            {Object.entries(categories).map(([category, cols]) => {
              const hiddenInCategory = cols.filter((col) => hiddenFields.some((item) => item.id === col.id));
              if (!hiddenInCategory.length) return null;
              return (
                <div key={category} className="funding-manage-fields-panel__chip-group">
                  <div className="funding-manage-fields-panel__chip-category">{category}</div>
                  <div className="funding-manage-fields-panel__chips">
                    {hiddenInCategory.map((col) => (
                      <button
                        key={col.id}
                        type="button"
                        onClick={() => addField(col.id)}
                        className="funding-manage-fields-panel__chip"
                      >
                        {col.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="funding-manage-fields-panel__footer">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="funding-manage-fields-panel__btn funding-manage-fields-panel__btn--cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="funding-manage-fields-panel__btn funding-manage-fields-panel__btn--apply"
        >
          Apply
        </button>
      </div>
    </aside>
  );
};

export default FundingManageFieldsPanel;
